import React from "react";
import { motion } from "framer-motion";
import { Reveal } from "@/components/site/Reveal";

const HERO_BG =
    "https://static.prod-images.emergentagent.com/jobs/4da0b27b-01e9-4410-a5bb-7719ce6ad6e8/images/318b55a4ad975de7d32cd85dc77265f3466e599f422f67cfdffc88b5b696b949.jpeg";

export const GleapHero = ({ openDemo }) => (
    <section
        className="relative overflow-hidden min-h-[88vh] flex items-center"
        data-testid="gleap-hero"
    >
        {/* Full-bleed animated background image */}
        <motion.div
            className="absolute inset-0"
            initial={{ scale: 1.08 }}
            animate={{ scale: 1 }}
            transition={{ duration: 8, ease: [0.22, 1, 0.36, 1] }}
        >
            <img
                src={HERO_BG}
                alt="Customer chatting with an AI assistant on her phone"
                className="w-full h-full object-cover object-[60%_center]"
            />
        </motion.div>
        {/* Readability gradient — deep teal from the left */}
        <div
            className="absolute inset-0"
            style={{
                background:
                    "linear-gradient(90deg, rgba(10,32,27,0.95) 0%, rgba(12,38,32,0.85) 32%, rgba(14,42,35,0.45) 60%, rgba(14,42,35,0.15) 100%)",
            }}
        />
        <div
            className="absolute inset-x-0 bottom-0 h-40"
            style={{ background: "linear-gradient(180deg, rgba(10,32,27,0) 0%, rgba(10,32,27,0.55) 100%)" }}
        />

        <div className="relative z-10 w-full px-5 md:px-10 lg:px-14 xl:px-20 py-24">
            <div className="grid lg:grid-cols-12 gap-12 items-center">
                {/* Left — all text */}
                <div className="lg:col-span-6 text-left">
                    <Reveal>
                        <div className="text-xs font-bold tracking-[0.18em] uppercase mb-5" style={{ color: "#BFE3A0" }}>
                            AI-Powered Enterprise Platform
                        </div>
                    </Reveal>
                    <Reveal delay={0.05}>
                        <h1 className="heading-hero text-left text-5xl sm:text-6xl lg:text-[4.4rem] !text-white">
                            From AI Assistant to AI Agent.{" "}
                            <span style={{ color: "#C9F27E" }}>One Platform.</span>
                        </h1>
                    </Reveal>

                    <Reveal delay={0.15}>
                        <p className="mt-7 text-intro text-base md:text-lg max-w-xl" style={{ color: "rgba(240,248,244,0.85)" }}>
                            Nyra AI unifies Conversational AI, Autonomous AI Agents, Multi-Agent
                            Orchestration, and AI-powered Tender Intelligence into a single enterprise
                            platform — empowering your teams to automate work, accelerate decisions,
                            delight customers, and win more business.
                        </p>
                    </Reveal>

                    <Reveal delay={0.3}>
                        <div className="mt-9 flex flex-wrap items-center gap-3">
                            <button
                                onClick={openDemo}
                                className="pill-lime"
                                data-testid="hero-cta-request-demo"
                            >
                                Request a demo
                            </button>
                            <a
                                href="#nyra-console"
                                onClick={(e) => {
                                    e.preventDefault();
                                    document.getElementById("nyra-console")?.scrollIntoView({ behavior: "smooth" });
                                }}
                                className="pill-ghost-light"
                                data-testid="hero-cta-explore"
                            >
                                Explore the platform
                            </a>
                        </div>
                    </Reveal>

                    <Reveal delay={0.4}>
                        <div className="mt-9 flex flex-wrap items-center gap-x-6 gap-y-2 text-xs" style={{ color: "rgba(240,248,244,0.7)" }}>
                            <span className="inline-flex items-center gap-1.5">
                                <i className="fa-solid fa-shield-halved" style={{ color: "#C9F27E" }} />
                                SOC 2 compliant
                            </span>
                            <span className="inline-flex items-center gap-1.5">
                                <span style={{ color: "#D4B542" }}>★</span>
                                <span className="font-semibold text-white">4.8</span>
                                <span>· 50+ enterprise deployments</span>
                            </span>
                        </div>
                    </Reveal>

                    {/* Mobile — compact chat bubbles */}
                    <div className="lg:hidden mt-10 space-y-3 max-w-sm" data-testid="hero-chat-mobile">
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 0.9, duration: 0.5 }}
                            className="bg-white rounded-xl rounded-bl-sm p-3 shadow-xl mr-8"
                            data-testid="hero-chat-mobile-user"
                        >
                            <div className="flex items-center gap-1.5 mb-1">
                                <span className="w-[18px] h-[18px] rounded-full flex items-center justify-center text-[8px] font-bold text-white" style={{ background: "#5FA88A" }}>A</span>
                                <span className="text-[10px] font-semibold text-slate-700">Aisha</span>
                            </div>
                            <p className="text-xs text-slate-800 leading-snug">
                                My reward points are not being applied at checkout.
                            </p>
                        </motion.div>
                        <motion.div
                            initial={{ opacity: 0, y: 16 }}
                            animate={{ opacity: 1, y: 0 }}
                            transition={{ delay: 1.5, duration: 0.5 }}
                            className="bg-white rounded-xl rounded-br-sm p-3 shadow-xl ml-8"
                            data-testid="hero-chat-mobile-agent"
                        >
                            <div className="flex items-center gap-1.5 mb-1">
                                <span className="w-[18px] h-[18px] rounded-full flex items-center justify-center" style={{ background: "#0E2A23" }}>
                                    <i className="fa-solid fa-bolt text-[7px]" style={{ color: "#C9F27E" }} />
                                </span>
                                <span className="text-[10px] font-semibold text-slate-700">Nyra AI Agent</span>
                            </div>
                            <p className="text-xs text-slate-800 leading-snug">
                                I found the issue and applied your reward points. Your total is updated.
                            </p>
                            <div className="mt-2 pt-2 space-y-1 border-t border-slate-100">
                                {["Verified reward balance", "Applied discount and updated cart"].map((t, i) => (
                                    <motion.div
                                        key={t}
                                        initial={{ opacity: 0, x: -6 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 2 + i * 0.3, duration: 0.35 }}
                                        className="flex items-center gap-1.5 text-[10px] text-slate-600"
                                    >
                                        <i className="fa-solid fa-circle-check" style={{ color: "#5FA88A" }} />
                                        <span>{t}</span>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>
                    </div>
                </div>
                <div className="lg:col-span-6 relative hidden lg:block min-h-[420px]">
                    <motion.div
                        initial={{ opacity: 0, y: 30, x: -10 }}
                        animate={{ opacity: 1, y: 0, x: 0 }}
                        transition={{ delay: 1.1, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                        className="absolute top-[34%] left-[2%] w-[300px]"
                        data-testid="hero-chat-bubble-user"
                    >
                        <motion.div
                            animate={{ y: [0, -8, 0] }}
                            transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                            className="bg-white rounded-2xl rounded-bl-md p-4 shadow-2xl"
                        >
                            <div className="flex items-center gap-2 mb-1.5">
                                <span className="w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold text-white" style={{ background: "#5FA88A" }}>A</span>
                                <span className="text-xs font-semibold text-slate-700">Aisha</span>
                            </div>
                            <p className="text-sm text-slate-800 leading-snug">
                                My reward points are not being applied at checkout.
                            </p>
                        </motion.div>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 30, x: 10 }}
                        animate={{ opacity: 1, y: 0, x: 0 }}
                        transition={{ delay: 1.9, duration: 0.7, ease: [0.22, 1, 0.36, 1] }}
                        className="absolute top-[62%] left-[16%] w-[340px]"
                        data-testid="hero-chat-bubble-agent"
                    >
                        <motion.div
                            animate={{ y: [0, -10, 0] }}
                            transition={{ duration: 6, repeat: Infinity, ease: "easeInOut", delay: 1 }}
                            className="bg-white rounded-2xl rounded-br-md shadow-2xl overflow-hidden"
                        >
                            <div className="p-4 pb-3">
                                <div className="flex items-center gap-2 mb-1.5">
                                    <span className="w-6 h-6 rounded-full flex items-center justify-center" style={{ background: "#0E2A23" }}>
                                        <i className="fa-solid fa-bolt text-[9px]" style={{ color: "#C9F27E" }} />
                                    </span>
                                    <span className="text-xs font-semibold text-slate-700">Nyra AI Agent</span>
                                </div>
                                <p className="text-sm text-slate-800 leading-snug">
                                    I found the issue and applied your reward points. Your total is updated.
                                </p>
                            </div>
                            <div className="px-4 pb-4 space-y-2" style={{ background: "rgba(95,168,138,0.08)" }}>
                                {[
                                    "Verified reward balance and identified checkout issue",
                                    "Applied discount and updated cart",
                                ].map((t, i) => (
                                    <motion.div
                                        key={t}
                                        initial={{ opacity: 0, x: -8 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        transition={{ delay: 2.6 + i * 0.4, duration: 0.4 }}
                                        className="flex items-start gap-2 pt-2 text-xs text-slate-600"
                                    >
                                        <i className="fa-solid fa-circle-check mt-0.5" style={{ color: "#5FA88A" }} />
                                        <span>{t}</span>
                                    </motion.div>
                                ))}
                            </div>
                        </motion.div>
                    </motion.div>
                </div>
            </div>
        </div>
    </section>
);

/* ------------------------------------------------------------------
   TRUST LOGO STRIP
------------------------------------------------------------------- */
export const GleapTrustStrip = () => {
    const industries = [
        { name: "BANKING & NBFC", weight: 600 },
        { name: "INSURANCE", weight: 700 },
        { name: "HEALTHCARE", weight: 600 },
        { name: "RETAIL & E-COMMERCE", weight: 500 },
        { name: "MANUFACTURING", weight: 600 },
        { name: "GOVERNMENT", weight: 700 },
    ];
    return (
        <section className="py-14 bg-clean" data-testid="gleap-trust">
            <div className="container-x">
                <Reveal>
                    <p className="text-center text-sm text-slate-500 mb-8">
                        Trusted by enterprises across regulated industries · Enterprise-grade security & compliance
                    </p>
                </Reveal>
                <div className="marquee">
                    <div className="logo-marquee">
                        {[...industries, ...industries, ...industries].map((b, i) => (
                            <div
                                key={i}
                                className="text-slate-500 hover:text-slate-800 transition-colors"
                                style={{ fontWeight: b.weight, letterSpacing: "0.15em", fontSize: "15px" }}
                            >
                                {b.name}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
};
